import "../styles.scss";

import { InputChangeEventDetail } from "@ionic/core";
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonInput,
  IonItem,
  IonPage,
  IonSpinner,
  IonText,
  IonToolbar,
} from "@ionic/react";
import { ICV5Agreement } from "@src/app/onboardingStripe/documents/ICV5Agreement";
import { OnboardingRouterPath } from "@src/app/routing/constant/onboardingRoute";
import { useDefinedAgent } from "@src/app/store/helperHooks";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { CBHToast } from "@src/designsystem/Toast/Toast";
import { CONTRACTOR_AGREEMENT_VERSIONS } from "@src/lib/interface/src";
import { distance } from "fastest-levenshtein";
import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";

import { ReadAgreementError } from "./ErrorMessages/ReadAgreementError";
import { SignAgreementError } from "./ErrorMessages/SignAgreementError";
import { USER_EVENTS } from "../../../constants";
import { TabRouterPath } from "../../routing/constant/tabRoute";
import {
  READ_AGREEMENT_MESSAGE,
  SIGN_AGREEMENT_MESSAGE,
} from "../constants/AgreementErrorMessages";

enum ErrorMessageType {
  "READ_IC_AGREEMENT" = "READ_IC_AGREEMENT",
  "SIGN_IC_AGREEMENT" = "SIGN_IC_AGREEMENT",
}

interface ToastError {
  isOpen: boolean;
  message: string;
  messageType: ErrorMessageType;
}

export interface AgreementsProps {
  handleAgreementAcceptance: (
    agreementVersion: CONTRACTOR_AGREEMENT_VERSIONS,
    signature?: string
  ) => void;
  loading: boolean;
  disableBackButton?: boolean;
  showSignature: boolean;
}

export function OldAgreements(props: AgreementsProps) {
  const { handleAgreementAcceptance, loading, disableBackButton, showSignature } = props;
  const [isICAgreementRead, setIsICAgreementRead] = useState(false);
  const [toastError, setToastError] = useState<ToastError | undefined>();
  const [signatureName, setSignatureName] = useState("");
  const agent = useDefinedAgent();

  const contactSupportLinks = useSupportLinks("SIGN_IC_AGREEMENT");

  const scrollableAgreement = useRef<HTMLIonItemElement>(null);
  const workerName = agent?.name ?? "";
  const workerFirstName = agent?.firstName ?? "";
  const workerLastName = agent?.lastName ?? "";

  const specialCharactersRegex = /(’|‘|′|`)/g;

  const modifiedSignature = signatureName.replace(specialCharactersRegex, "'").toLowerCase();
  const modifiedWorkerName = workerName.replace(specialCharactersRegex, "'").toLowerCase();

  const isSignatureCloseEnoughToAcceptedSignature =
    workerFirstName.length > 3 &&
    workerLastName.length > 3 &&
    distance(modifiedSignature, modifiedWorkerName) <= 1;
  const isSigned =
    !showSignature ||
    (modifiedSignature.length &&
      (modifiedWorkerName === modifiedSignature || isSignatureCloseEnoughToAcceptedSignature));

  const shouldEnableContinueButton = isICAgreementRead && isSigned;

  const handleOnContinueClicked = () => {
    if (!isICAgreementRead) {
      setToastError({
        isOpen: true,
        message: READ_AGREEMENT_MESSAGE,
        messageType: ErrorMessageType.READ_IC_AGREEMENT,
      });
      return;
    }
    if (!isSigned) {
      setToastError({
        isOpen: true,
        message: SIGN_AGREEMENT_MESSAGE,
        messageType: ErrorMessageType.SIGN_IC_AGREEMENT,
      });
      return;
    }
    handleAgreementAcceptance?.(CONTRACTOR_AGREEMENT_VERSIONS.V5, signatureName);
  };

  const handleScroll = useCallback(() => {
    try {
      if (!scrollableAgreement?.current) {
        return;
      }
      const element = scrollableAgreement.current;
      const isScrolledToBottom =
        element.scrollHeight - (element.clientHeight + element.scrollTop) < 10;

      setIsICAgreementRead((wasICAgreementRead) => {
        return wasICAgreementRead || isScrolledToBottom;
      });
    } catch (error) {
      logError(APP_V2_APP_EVENTS.AGREEMENT_SIGNING_ERROR, {
        error,
        metadata: {
          message: "Error handling IC agreement scroll listener",
          userId: agent?.userId,
        },
      });
    }
  }, [scrollableAgreement, agent.userId]);

  const handleScrollDebounce = useRef(debounce(handleScroll, 1000)).current;

  const handleSignatureNameChange = (e: CustomEvent<InputChangeEventDetail>) => {
    const signature = e?.detail?.value?.trim() ?? "";
    setSignatureName(signature);
  };
  const handleSignatureNameClicked = () => {
    if (!isICAgreementRead) {
      setToastError({
        isOpen: true,
        message: READ_AGREEMENT_MESSAGE,
        messageType: ErrorMessageType.READ_IC_AGREEMENT,
      });
    }
  };
  useEffect(() => {
    logEvent(USER_EVENTS.VIEWED_TERMS, {
      hcpId: agent?.userId as string,
      name: workerName,
      agreementVersion: CONTRACTOR_AGREEMENT_VERSIONS.V5,
    });
  }, [agent.userId, workerName]);

  useEffect(() => {
    const nodeElement = scrollableAgreement.current;
    return () => {
      if (nodeElement) {
        nodeElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  useEffect(() => {
    handleScrollDebounce?.cancel();
  }, [handleScrollDebounce]);

  return (
    <IonPage className="onboarding-page ic-agreement-page">
      <IonHeader no-border className="onboarding-header">
        <IonToolbar className="onboarding-toolbar">
          <IonButtons slot="start">
            {!disableBackButton && (
              <IonBackButton
                text=""
                defaultHref={
                  agent?.onboardingFlags?.isSignupCompleted
                    ? TabRouterPath.OPEN_SHIFTS
                    : OnboardingRouterPath.ONBOARDING_CONTRACTOR_AGREEMENT_REVIEW
                }
                mode="ios"
                color="dark"
              />
            )}
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="container content-layout">
          <IonItem
            lines="none"
            className="ic-agreement-container ion-margin-top ion-margin-bottom"
            data-testid="ic-agreement-container"
            onScroll={handleScrollDebounce}
            ref={scrollableAgreement}
          >
            <div className="ic-agreement-content">
              <ICV5Agreement showSignature={showSignature} />
            </div>
          </IonItem>

          <IonFooter className="footer-container">
            {showSignature ? (
              <>
                <IonText>
                  <p>
                    Sign this agreement electronically by typing your name exactly as shown below.
                    After signing, a copy of the agreement will be available in the app.
                  </p>
                </IonText>

                <IonText>
                  <p className="accepted-signature-text">
                    <strong>Accepted Signature: {workerName}</strong>
                  </p>
                </IonText>

                <IonItem className="signature-input-container">
                  {!isICAgreementRead && (
                    <IonButton
                      expand="full"
                      mode="ios"
                      fill="clear"
                      onClick={handleSignatureNameClicked}
                      data-testid="signature-disabled-action"
                      className="signature-disabled-action"
                    ></IonButton>
                  )}
                  <IonInput
                    className={`signature-input ${!isICAgreementRead ? "disabled" : ""}`}
                    data-testid="signature-input"
                    color={isSigned ? "primary" : "dark"}
                    type="text"
                    disabled={!isICAgreementRead}
                    debounce={100}
                    placeholder={workerName}
                    onIonChange={handleSignatureNameChange}
                    value={signatureName}
                  />
                </IonItem>
              </>
            ) : null}
            <IonText color="danger" data-testid="agreement-notice">
              <p
                style={{
                  marginTop: !shouldEnableContinueButton ? "0" : "1rem",
                }}
              >
                {getAgreementHintText({
                  isICAgreementRead,
                  isSigned,
                  showSignature,
                })}
              </p>
            </IonText>

            <IonButton
              expand="block"
              size="large"
              className={`ion-text-wrap ion-margin-top ion-margin-bottom continue-button ${
                !shouldEnableContinueButton ? "disabled" : ""
              }`}
              disabled={loading}
              data-testid="continue-button"
              onClick={handleOnContinueClicked}
            >
              {loading ? (
                <IonSpinner color="light" name="crescent"></IonSpinner>
              ) : (
                "I have read and agree to this Independent Contractor Agreement"
              )}
            </IonButton>
          </IonFooter>
        </div>
      </IonContent>
      <CBHToast
        isOpen={toastError?.isOpen ?? false}
        message={toastError?.message}
        onDidDismiss={() => setToastError(undefined)}
      >
        {toastError?.messageType === ErrorMessageType.READ_IC_AGREEMENT ? (
          <ReadAgreementError contactSupportLinks={contactSupportLinks} agent={agent} />
        ) : null}
        {toastError?.messageType === ErrorMessageType.SIGN_IC_AGREEMENT ? (
          <SignAgreementError
            agent={agent}
            providedName={signatureName}
            contactSupportLinks={contactSupportLinks}
          />
        ) : null}
      </CBHToast>
    </IonPage>
  );
}

const getAgreementHintText = ({ isICAgreementRead, isSigned, showSignature }) => {
  if (!isICAgreementRead) {
    if (!showSignature) {
      return "Please read the full agreement and scroll to the bottom";
    }
    if (!isSigned) {
      return "Please read the full agreement and scroll to the bottom, and then type your name to continue";
    }
  } else {
    if (!isSigned) {
      return "Please type your name to continue (needs to match the Accepted Signature)";
    }
  }
  return "";
};
